import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { notifyUser } from "../../../util/util";
import { extractErrorMessage } from '../../../hooks/extractErrorMessage';
import { Spin } from "antd";
import { ReactComponent as LockIcon } from '../../../assets/UiElements/lock.svg';
import { authApi } from '../../../services/auth.service';

export const OneTimePinFP = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const email_or_phone = location.state?.email_or_phone;
    const email = location.state?.email;
    const [otpRef, setOtpRef] = useState(location.state?.otpRef || '');

    const [otp, setOTP] = useState('');
    const [loading, setLoading] = useState(false);
    const [seconds, setSeconds] = useState(165);
    const [shake, setShake] = useState(false);

    const formatTime = useMemo(() => {
        return (timeInSeconds) => {
            const minutes = Math.floor(timeInSeconds / 60);
            const seconds = timeInSeconds % 60;
            return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
        };
    }, []);

    const remainingTime = useMemo(() => {
        return seconds > 45 ? formatTime(seconds - 45) : null;
    }, [seconds, formatTime]);

    useEffect(() => {
        if (seconds <= 0) return;
        const timer = setInterval(() => {
            setSeconds(prev => prev - 1);
        }, 1000);
        return () => clearInterval(timer);
    }, [seconds]);

    const changeHandler = (e) => setOTP(e.target.value);

    const verifyOTP = async () => {
        if (!otp || otp.length !== 6) {
            return notifyUser('error', 'Please enter the 6-digit OTP received to proceed!', 'Validation Error');
        }

        if (!otpRef && !email_or_phone && !email) {
            return notifyUser('error', 'Session expired. Please try again.', 'Error');
        }

        setLoading(true);
        try {
            // Use otp_ref if available, otherwise fall back to email_or_phone
            const identifier = otpRef || email_or_phone || email;
            const isOtpRef = !!otpRef;
            
            const response = await authApi.verifyResetOtp(
                isOtpRef 
                    ? { otp_ref: identifier, otp }
                    : { email_or_phone: identifier, otp }
            );
            
            if (response.data?.success) {
                const token = response.data?.data?.token;
                sessionStorage.setItem('reset_token', token);
                notifyUser('success', 'OTP verified successfully!', 'Success');
                navigate('/forgot-password/create-password', { 
                    state: { 
                        email_or_phone: email_or_phone || email, 
                        token 
                    } 
                });
            } else {
                notifyUser('error', extractErrorMessage(response, 'Error verifying OTP'), 'Error');
            }
        } catch (error) {
            notifyUser('error', extractErrorMessage(error, 'Error verifying OTP'), 'Error');
        } finally {
            setLoading(false);
        }
    };

    const resendOTP = async () => {
        if (seconds > 45) return;
        
        setShake(true);
        setSeconds(165);
        setTimeout(() => setShake(false), 500);
        
        try {
            // Use otp_ref for resend if available
            if (otpRef) {
                const response = await authApi.resendOtp(otpRef);
                // Update otp_ref from response if available (new otp_ref for subsequent calls)
                const newOtpRef = response.data?.data?.otp_ref;
                if (newOtpRef) {
                    setOtpRef(newOtpRef);
                }
                if (response.data?.success) {
                    notifyUser('success', response.data?.message || 'OTP has been resent successfully!', 'Success');
                } else {
                    notifyUser('error', extractErrorMessage(response, 'Failed to resend OTP'), 'Error');
                }
            } else {
                // Fallback to forgot password API for legacy flow
                const identifier = email_or_phone || email;
                const response = await authApi.forgotPassword({ email_or_phone: identifier });
                if (response.data?.success) {
                    notifyUser('success', response.data?.message || 'OTP has been resent successfully!', 'Success');
                } else {
                    notifyUser('error', extractErrorMessage(response, 'Failed to resend OTP'), 'Error');
                }
            }
        } catch (error) {
            notifyUser('error', extractErrorMessage(error, 'Failed to resend OTP'), 'Error');
        }
    };

    const isButtonDisabled = otp.length !== 6;

    return (
        <div className='mx-auto mobile-full-width shadow bg-white p-5 rounded login-width'>
            <div align='center'>
                <p className='lead fw-bold'>Enter your One Time Pin</p>
                <small className='text-center'>
                    Please enter the 6-digit OTP sent to your registered contact
                </small>
                <Spin spinning={loading}>
                    <div className="mt-4 w-75 mx-auto mobile-full-width">
                        <omk-otp-field
                            number-of-inputs="6"
                            resend-text=""
                            resend-button-label=""
                            label="One-time Password"
                            className={`mb-1 ${shake ? 'shake' : ''}`}
                            value={otp}
                            onInput={changeHandler}
                        />
                        <div className="d-flex justify-content-center align-items-center mb-3">
                            <p className="text-center omk-text-small text-black-100 mb-1">
                                Didn't receive the OTP?&nbsp;
                            </p>
                            <div className="text-center">
                                {remainingTime ? (
                                    <p className="text-center omk-text-small text-black-50 mb-1"> 
                                        Resend in {remainingTime}
                                    </p>
                                ) : (
                                    <p
                                        className="text-center omk-text-small mb-1"
                                        style={{
                                            color: '#28a745',
                                            fontWeight: 'bold',
                                            textDecoration: 'underline',
                                            cursor: 'pointer'
                                        }}
                                        onClick={resendOTP}
                                    >
                                        Resend OTP
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="d-flex justify-content-center">
                            <p className="text-center omk-text-small text-black-50 mb-5">
                                <LockIcon style={{ marginRight: '5px', width: '16px', height: '16px' }} />
                                Your information is confidential and secure
                            </p>
                        </div>
                        <button
                            onClick={verifyOTP}
                            className={`btn ${isButtonDisabled ? 'btn-secondary' : 'btn-primary'} mb-4 w-50 mobile-full-width`}
                            disabled={isButtonDisabled || loading}
                            style={{
                                borderRadius: '50px',
                                padding: isButtonDisabled ? '5px 20px' : '10px 20px',
                                height: 'auto',
                            }}
                        >
                            {loading ? 'Verifying...' : 'Continue'}
                        </button>
                    </div>
                </Spin>
            </div>
            <style>{shakeStyle}</style>
        </div>
    );
};

const shakeStyle = `
    @keyframes shake {
        0% { transform: translateX(0); }
        25% { transform: translateX(-5px); }
        50% { transform: translateX(5px); }
        75% { transform: translateX(-5px); }
        100% { transform: translateX(0); }
    }
    .shake {
        animation: shake 0.5s ease-out;
    }
`;

export default OneTimePinFP;
